import { Button } from "@components/atoms/Button"
import { Logo } from "@components/atoms/Logo"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Appear } from "@components/motion/Appear"
import { cn, tv } from "@utils/tw"
import { useAppData } from "eddev/hooks"
import { Link, useRoute, useRouter } from "eddev/routing"
import { useAnimationFrame } from "motion/react"
import { useRef, useState } from "react"

export function Header({ noPattern = false, barClassName }: { noPattern?: boolean; barClassName?: string }) {
  const route = useRoute()
  const [megaSelected, setMegaSelected] = useState(-1)
  const timer = useRef<number | null>(null)
  const lastScroll = useRef(0)
  const direction = useRef(0)
  const [visible, setVisible] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(-1)

  useAnimationFrame(() => {
    const currentScroll = window.scrollY
    if (megaSelected !== -1 || currentScroll < 100 || currentScroll < lastScroll.current) {
      setVisible(true)
    } else if (direction.current === 1 && currentScroll > lastScroll.current) {
      setVisible(false)
    }
    direction.current = currentScroll > lastScroll.current ? 1 : -1
    lastScroll.current = currentScroll
  })

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }

  const unselectMenu = (immediate?: boolean) => {
    clearTimer()
    timer.current = window.setTimeout(() => setMegaSelected(-1), immediate ? 0 : 300)
  }

  return (
    <>
      <div className={cn("w-full absolute left-0 -top-2 flex flex-col", noPattern && "hidden")}>
        <div className={cn("w-full h-headerHeight", barClassName)} />
        <div
          className="w-full h-headerPattern"
          style={{
            backgroundSize: "auto 100%",
            backgroundRepeat: "repeat-x",
            backgroundImage: `url("${env.themePath}/assets/images/patterns/1_reverse.svg")`,
          }}
        />
      </div>
      <div className={cn("w-full h-headerHeight", !noPattern && "bg-slate200", barClassName)} />
      <div
        className={cn(
          "w-full h-full flex items-start justify-center fixed left-0 top-0 z-50 pointer-events-none",
          !visible && mobileOpen === -1 && "-translate-y-headerHeight",
          "duration-300 bg-white/0 backdrop-blur-0",
          mobileOpen > -1 && "!bg-white/80 !backdrop-blur-[2px]",
        )}
        style={{
          transitionProperty: "transform, background-color, backdrop-filter",
        }}
      >
        <div
          className={cn(
            "w-full h-headerHeight absolute left-0 top-0 bg-slate50 pointer-events-none transition-colors duration-300 md:duration-0",
            !noPattern && "bg-slate200",
            mobileOpen > -1 && "!bg-slate50",
            barClassName,
          )}
        />

        <MegaMenu
          megaSelected={megaSelected}
          setMegaSelected={setMegaSelected}
          clearTimer={clearTimer}
          unselectMenu={unselectMenu}
        />

        <div className={cn("w-full h-headerHeight flex items-center justify-center relative pointer-events-none")}>
          <div className="w-full max-w-grid mx-auto flex items-center justify-between">
            <Link href="/" className="text-slate950 pointer-events-auto" onClick={() => setMobileOpen(-1)}>
              <Logo />
            </Link>

            <MainMenu
              megaSelected={megaSelected}
              setMegaSelected={setMegaSelected}
              clearTimer={clearTimer}
              unselectMenu={unselectMenu}
              className="hidden md:flex"
            />

            <div className="flex gap-5 items-center justify-center pointer-events-auto">
              <MainFeatureMenu setMobileOpen={setMobileOpen} />
              <MenuToggle open={mobileOpen > -1} setOpen={(open) => setMobileOpen(open ? 0 : -1)} />
            </div>
          </div>
        </div>

        <MobileMenu setMobileOpen={setMobileOpen} mobileOpen={mobileOpen} />
      </div>
    </>
  )
}

const MegaMenu = ({
  megaSelected,
  setMegaSelected,
  clearTimer,
  unselectMenu,
}: {
  megaSelected: number
  setMegaSelected: React.Dispatch<React.SetStateAction<number>>
  clearTimer: () => void
  unselectMenu: (i?: boolean) => void
}) => {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const mainMenu = menus?.find((menu) => menu?.slug === "main-menu")
  const topLevel = mainMenu?.menuItems?.nodes?.filter((item) => !item?.parentId) || []

  return (
    <div
      className={cn(
        "fixed left-0 top-0 w-full h-full bg-black/20 pointer-events-none transition-opacity duration-300 opacity-0",
        megaSelected !== -1 && "opacity-100 pointer-events-auto",
      )}
    >
      {topLevel.map((item) => {
        if (!item) return null
        const isSelected = megaSelected === topLevel.indexOf(item)
        const FeaturedLinkElement: any = !!item.menuItemOptions?.featuredItemLink ? Link : "div"
        const featuredLinkProps = !!item.menuItemOptions?.featuredItemLink
          ? {
              href: item.menuItemOptions.featuredItemLink?.url || "#",
              target: item.menuItemOptions.featuredItemLink?.target || undefined,
              onClick: () => unselectMenu(true),
            }
          : {}

        return (
          <div
            key={`top${item.label}`}
            className="absolute left-0 top-0 w-full flex items-center justify-center gap-4 pointer-events-none"
          >
            <div
              className={cn("pt-headerHeight", isSelected && "pointer-events-auto")}
              onMouseOut={() => unselectMenu()}
              onMouseOver={() => clearTimer()}
            >
              <div
                className={cn(
                  "rounded-sm bg-white flex gap-megaMenuFeatureGap transition-all duration-300 opacity-0 translate-y-4 md:min-w-[30em]",
                  isSelected && "opacity-100 translate-y-0",
                )}
                onMouseOver={() => clearTimer()}
                onMouseOut={() => unselectMenu()}
              >
                <div className="flex flex-col justify-between">
                  <div className="p-5 flex flex-col gap-4">
                    {item.childItems?.nodes.map((childItem, cx) => {
                      if (!childItem) return null
                      return (
                        <Appear active={isSelected} delay={100 + cx * 100} key={`childAppear${childItem.label}${cx}`}>
                          <Link
                            href={childItem.url || "#"}
                            target={childItem.menuItemOptions?.external ? "_blank" : undefined}
                            rel={childItem.menuItemOptions?.external ? "noopener noreferrer" : undefined}
                            className="flex flex-col gap-1 group"
                            onClick={() => unselectMenu(true)}
                          >
                            <p className="type-sans-base-semibold text-pretty underline decoration-transparent md:group-hover:decoration-current transition-all flex gap-1 items-center">
                              {childItem.label}
                              {childItem.menuItemOptions?.external && (
                                <svg
                                  className="w-4 h-4"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M10.9695 5.52081L4.63181 11.8585L4 11.2267L10.3377 4.889L4.31666 4.88913L4.3159 4.00012H11.8584V11.5426L10.9694 11.5418L10.9695 5.52081Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              )}
                            </p>
                            {childItem.menuItemOptions?.subtitle && (
                              <p className="type-serif-sm-regular text-slate600">
                                {childItem.menuItemOptions.subtitle}
                              </p>
                            )}
                          </Link>
                        </Appear>
                      )
                    })}
                  </div>
                  {item.menuItemOptions?.additionalCtaButton && (
                    <Appear active={isSelected} delay={150} className="p-5">
                      <Button
                        href={item.menuItemOptions.additionalCtaButton.url || "#"}
                        target={item.menuItemOptions.additionalCtaButton.target || undefined}
                        onClick={() => unselectMenu(true)}
                      >
                        {item.menuItemOptions.additionalCtaButton.title}
                      </Button>
                    </Appear>
                  )}
                </div>
                {item.menuItemOptions?.showFeaturedItem && item.menuItemOptions?.featuredItemImage?.mediaItemUrl && (
                  <FeaturedLinkElement {...featuredLinkProps} className="p-2">
                    <Appear
                      active={isSelected}
                      className="w-megaMenuImageW aspect-[300/282] rounded-sm overflow-hidden relative bg-slate950"
                    >
                      <Appear zoom duration={1000} active={isSelected} className="w-full h-full relative">
                        <ResponsiveImage
                          {...item.menuItemOptions.featuredItemImage}
                          className="w-full h-full"
                          objectFit="cover"
                        />
                        <div
                          className="absolute left-0 top-0 w-full h-full"
                          style={{
                            background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(26, 26, 26, 0.40) 70%)",
                          }}
                        />
                      </Appear>
                      <div className="absolute left-0 top-0 w-full h-full p-4 flex flex-col justify-end gap-4">
                        {item.menuItemOptions.featuredItemTitle && (
                          <p className="type-serif-md-regular text-white text-balance">
                            {item.menuItemOptions.featuredItemTitle}
                          </p>
                        )}
                        {item.menuItemOptions.featuredItemSubtitle && (
                          <p className="type-mono-xs-regular text-white">{item.menuItemOptions.featuredItemSubtitle}</p>
                        )}
                      </div>
                    </Appear>
                  </FeaturedLinkElement>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const MainMenu = ({
  megaSelected,
  setMegaSelected,
  clearTimer,
  unselectMenu,
  className,
}: {
  megaSelected: number
  setMegaSelected: React.Dispatch<React.SetStateAction<number>>
  clearTimer: () => void
  unselectMenu: (i?: boolean) => void
  className?: string
}) => {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const mainMenu = menus?.find((menu) => menu?.slug === "main-menu")
  const topLevel = mainMenu?.menuItems?.nodes?.filter((item) => !item?.parentId) || []

  const selectMenu = (index: number) => {
    clearTimer()
    setMegaSelected(index)
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center w-full h-headerHeight gap-3 absolute left-0 top-0 pointer-events-none",
        className,
      )}
    >
      {topLevel.map((item, ix) => {
        if (!item) return null
        const hasChildren = (item.childItems?.nodes?.length || 0) > 0
        const elProps = hasChildren
          ? {
              variant: "primary" as const,
            }
          : {
              href: item.url || "#",
              target: item.menuItemOptions?.external ? "_blank" : undefined,
              rel: item.menuItemOptions?.external ? "noopener noreferrer" : undefined,
            }

        return (
          <Button
            key={`nav${item.label}${ix}`}
            {...elProps}
            variant="primary"
            onMouseOver={() => hasChildren && selectMenu(topLevel.indexOf(item))}
            onMouseOut={() => unselectMenu(true)}
            onClick={() => hasChildren && selectMenu(topLevel.indexOf(item))}
            className="pointer-events-auto"
          >
            {item.label}
          </Button>
        )
      })}
    </div>
  )
}

const MainFeatureMenu = ({ setMobileOpen }: { setMobileOpen: React.Dispatch<React.SetStateAction<number>> }) => {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const mainMenu = menus?.find((menu) => menu?.slug === "main-cta-menu")

  return (
    <div className="flex items-center gap-4">
      {mainMenu?.menuItems?.nodes.map((item) => {
        if (!item) return null
        return (
          <Button
            key={item.title}
            href={item.url || "#"}
            target={item.target || undefined}
            variant="dark"
            onClick={() => setMobileOpen(-1)}
          >
            {item.label}
          </Button>
        )
      })}
    </div>
  )
}

const MenuToggle = ({
  className,
  open,
  setOpen,
}: {
  className?: string
  open: boolean
  setOpen: (open: boolean) => void
}) => {
  const styles = menuToggleStyles({
    open: open,
  })

  return (
    <div onClick={() => setOpen(!open)} className={cn(styles.base(), className)}>
      <div className={cn(styles.line(), styles.line1())} />
      <div className={cn(styles.line(), styles.line2())} />
      <div className={cn(styles.line(), styles.line3())} />
    </div>
  )
}

const menuToggleStyles = tv({
  base: "pointer-events-auto group cursor-pointer flex justify-center items-center shrink-0 size-7 md:size-5 will-change-transform isolate md:hidden",
  slots: {
    line: "absolute transition-[opacity,transform] duration-150 w-full before:block before:absolute before:top-[-1px] before:transition-none before:bottom-[-1px] before:w-full before:[content:'_'] before:bg-current",
    line1: "",
    line2: "",
    line3: "",
  },
  variants: {
    open: {
      true: {
        line1: "rotate-45",
        line2: "opacity-0",
        line3: "-rotate-45",
      },
      false: {
        line1: "-translate-y-[6px] md:-translate-y-[8px]",
        line2: "",
        line3: "translate-y-[6px] md:translate-y-[8px]",
      },
    },
  },
})

const MobileMenu = ({
  setMobileOpen,
  mobileOpen,
}: {
  setMobileOpen: React.Dispatch<React.SetStateAction<number>>
  mobileOpen: number
}) => {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const mainMenu = menus?.find((menu) => menu?.slug === "main-menu")
  const topLevel = mainMenu?.menuItems?.nodes?.filter((item) => !item?.parentId) || []

  return (
    <div
      className={cn(
        "fixed left-0 top-0 w-full h-full md:hidden z-50 pointer-events-none opacity-0 transition-opacity duration-300",
        mobileOpen > -1 && "opacity-100",
      )}
    >
      <div className="w-full relative mt-headerHeight h-full overflow-hidden">
        {/* top level menu first */}
        <div
          className={cn(
            "w-full overflow-auto absolute left-0 top-0 px-3 transition-transform duration-300",
            mobileOpen > 0 && "-translate-x-full",
          )}
        >
          <div className={cn("bg-slate200 rounded-sm py-2", mobileOpen > -1 && "pointer-events-auto")}>
            {topLevel.map((item, ix) => {
              if (!item) return null
              const hasChildren = (item.childItems?.nodes?.length || 0) > 0
              return (
                <Appear active={mobileOpen > -1} key={`mobileappear${item.label}`} delay={100 + ix * 50}>
                  <Link
                    key={`mobiletop${item.label}`}
                    href={item.url || "#"}
                    target={hasChildren ? undefined : "_blank"}
                    rel={hasChildren ? undefined : "noopener noreferrer"}
                    className="w-full flex items-center justify-between py-6 px-5 type-mono-xs-regular text-slate950"
                    onClick={() => hasChildren && setMobileOpen(ix + 1)}
                  >
                    {item.label}
                    {(item.childItems?.nodes?.length || 0) > 0 && (
                      <svg className="w-5 h-5" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.10811 8L5 4.93333L5.94595 4L10 8L5.94595 12L5 11.0667L8.10811 8Z" fill="#1F1F1F" />
                      </svg>
                    )}
                  </Link>
                </Appear>
              )
            })}
          </div>
        </div>

        {/* submenus */}
        {topLevel.map((item, ix) => {
          if (!item) return null
          const isOpen = mobileOpen === ix + 1
          const FeaturedLinkElement: any = !!item.menuItemOptions?.featuredItemLink ? Link : "div"
          const featuredLinkProps = !!item.menuItemOptions?.featuredItemLink
            ? {
                href: item.menuItemOptions.featuredItemLink?.url || "#",
                target: item.menuItemOptions.featuredItemLink?.target || undefined,
                onClick: () => setMobileOpen(-1),
              }
            : {}

          return (
            <div
              key={`mobilesub${item.label}`}
              className={cn(
                "w-full overflow-auto absolute left-0 top-0 px-3 transition-transform duration-300 translate-x-full",
                isOpen && "translate-x-0",
              )}
            >
              <div className={cn("bg-slate200 rounded-sm py-3", isOpen && "pointer-events-auto", "flex flex-col")}>
                <div
                  className="px-3 py-5 flex gap-4 items-center type-mono-xs-regular"
                  onClick={() => setMobileOpen(0)}
                >
                  <svg className="w-5 h-5" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.89189 8L11 11.0667L10.0541 12L6 8L10.0541 4L11 4.93333L7.89189 8Z"
                      fill="currentColor"
                    />
                  </svg>
                  Back
                </div>

                <div className="w-full flex flex-col gap-2">
                  {item.childItems?.nodes.map((childItem, cx) => {
                    if (!childItem) return null
                    return (
                      <Appear active={isOpen} delay={100 + cx * 50} key={`mobilechildappear${childItem.label}${cx}`}>
                        <Link
                          key={`mobilechild${childItem.label}`}
                          href={childItem.url || "#"}
                          target={childItem.menuItemOptions?.external ? "_blank" : undefined}
                          rel={childItem.menuItemOptions?.external ? "noopener noreferrer" : undefined}
                          className="w-full flex flex-col items-start justify-center gap-2 py-4 px-5 text-slate950"
                          onClick={() => setMobileOpen(-1)}
                        >
                          <p className="type-sans-base-semibold flex gap-1 items-center">
                            {childItem.label}
                            {childItem.menuItemOptions?.external && (
                              <svg
                                className="w-5 h-5"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10.9695 5.52081L4.63181 11.8585L4 11.2267L10.3377 4.889L4.31666 4.88913L4.3159 4.00012H11.8584V11.5426L10.9694 11.5418L10.9695 5.52081Z"
                                  fill="currentColor"
                                />
                              </svg>
                            )}
                          </p>
                          {childItem.menuItemOptions?.subtitle && (
                            <span className="type-serif-sm-regular text-slate600">
                              {childItem.menuItemOptions?.subtitle}
                            </span>
                          )}
                        </Link>
                      </Appear>
                    )
                  })}
                </div>

                {item.menuItemOptions?.showFeaturedItem && item.menuItemOptions?.featuredItemImage?.mediaItemUrl && (
                  <FeaturedLinkElement {...featuredLinkProps} className="p-5 mt-6">
                    <Appear
                      active={isOpen}
                      delay={200}
                      className="w-full aspect-[353/171] rounded-sm overflow-hidden relative"
                    >
                      <Appear zoom duration={1000} active={isOpen} className="w-full h-full relative">
                        <ResponsiveImage
                          {...item.menuItemOptions.featuredItemImage}
                          className="w-full h-full"
                          objectFit="cover"
                        />
                        <div
                          className="absolute left-0 top-0 w-full h-full"
                          style={{
                            background: "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(26, 26, 26, 0.40) 70%)",
                          }}
                        />
                      </Appear>
                      <div className="absolute left-0 top-0 w-full h-full p-5 flex flex-col justify-end gap-4">
                        {item.menuItemOptions.featuredItemTitle && (
                          <p className="type-serif-md-regular text-white text-balance">
                            {item.menuItemOptions.featuredItemTitle}
                          </p>
                        )}
                        {item.menuItemOptions.featuredItemSubtitle && (
                          <p className="type-mono-xs-regular text-white">{item.menuItemOptions.featuredItemSubtitle}</p>
                        )}
                      </div>
                    </Appear>
                  </FeaturedLinkElement>
                )}

                {item.menuItemOptions?.additionalCtaButton && (
                  <Appear
                    active={isOpen}
                    delay={250}
                    className={cn("px-5 pb-3", !item.menuItemOptions?.showFeaturedItem && "pt-7")}
                  >
                    <Link
                      href={item.menuItemOptions.additionalCtaButton.url || "#"}
                      target={item.menuItemOptions.additionalCtaButton.target || undefined}
                      onClick={() => setMobileOpen(-1)}
                      className="type-mono-xs-regular flex gap-3 items-center"
                    >
                      <span>{item.menuItemOptions.additionalCtaButton.title}</span>
                      <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M6.84844 5.0625H0V3.9375H6.84844L3.69844 0.7875L4.5 0L9 4.5L4.5 9L3.69844 8.2125L6.84844 5.0625Z"
                          fill="currentColor"
                        />
                      </svg>
                    </Link>
                  </Appear>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
